import { tenantSelector } from '@/features/configuration/configurationSelector';
import type { TransKeys } from '@/i18n/trans/index';
import { createTrans } from '@/i18n/trans/index';
import { useSelector } from '@/store/utils';
import type { TenantName } from '@/types/tenant';
import { ExternalLink } from '@packages/webshop-design-system';
import type { FC, ReactElement } from 'react';
import { Trans } from 'react-i18next';

export type TransTenantSpecificKeys =
  | 'FIRST'
  | 'FOURTH'
  | 'SECOND'
  | 'THIRD'
  | 'successMessage'
  | 'travelPassDisclaimer'
  | 'ticketSent';

const tenantCopiesTranslations: Record<
  `${TenantName}.${TransTenantSpecificKeys}`,
  ReactElement<typeof Trans>
> = {
  'ci.FIRST': <Trans i18nKey="tenant.ci.FIRST" defaults="First class" />,
  'ci.FOURTH': <Trans i18nKey="tenant.ci.FOURTH" defaults="Fourth class" />,
  'ci.SECOND': <Trans i18nKey="tenant.ci.SECOND" defaults="Second class" />,
  'ci.successMessage': (
    <Trans i18nKey="tenant.ci.successMessage" defaults="Success!" />
  ),
  'ci.THIRD': <Trans i18nKey="tenant.ci.THIRD" defaults="Third class" />,
  'ci.travelPassDisclaimer': <></>,
  'ci.ticketSent': (
    <Trans
      i18nKey="tenant.ci.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'velvet.FIRST': (
    <Trans i18nKey="tenant.velvet.FIRST" defaults="First class" />
  ),
  'velvet.FOURTH': (
    <Trans i18nKey="tenant.velvet.FOURTH" defaults="Fourth class" />
  ),
  'velvet.SECOND': (
    <Trans i18nKey="tenant.velvet.SECOND" defaults="Second class" />
  ),
  'velvet.successMessage': (
    <Trans i18nKey="tenant.velvet.successMessage" defaults="Success!" />
  ),
  'velvet.THIRD': (
    <Trans i18nKey="tenant.velvet.THIRD" defaults="Third class" />
  ),
  'velvet.travelPassDisclaimer': <></>,
  'velvet.ticketSent': (
    <Trans
      i18nKey="tenant.velvet.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'aguia.FIRST': <Trans i18nKey="tenant.aguia.FIRST" defaults="First class" />,
  'aguia.FOURTH': (
    <Trans i18nKey="tenant.aguia.FOURTH" defaults="Fourth class" />
  ),
  'aguia.SECOND': (
    <Trans i18nKey="tenant.aguia.SECOND" defaults="Second class" />
  ),
  'aguia.successMessage': (
    <Trans i18nKey="tenant.aguia.successMessage" defaults="Success!" />
  ),
  'aguia.THIRD': <Trans i18nKey="tenant.aguia.THIRD" defaults="Third class" />,
  'aguia.travelPassDisclaimer': <></>,
  'aguia.ticketSent': (
    <Trans
      i18nKey="tenant.aguia.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'arenaways.FIRST': (
    <Trans i18nKey="tenant.arenaways.FIRST" defaults="First class" />
  ),
  'arenaways.FOURTH': (
    <Trans i18nKey="tenant.arenaways.FOURTH" defaults="Fourth class" />
  ),
  'arenaways.SECOND': (
    <Trans i18nKey="tenant.arenaways.SECOND" defaults="Second class" />
  ),
  'arenaways.successMessage': (
    <Trans
      i18nKey="tenant.arenaways.successMessage"
      defaults="Let this journey begin, Arenaways style!"
    />
  ),
  'arenaways.THIRD': (
    <Trans i18nKey="tenant.arenaways.THIRD" defaults="Third class" />
  ),
  'arenaways.travelPassDisclaimer': (
    <Trans
      i18nKey="tenant.arenaways.travelPassDisclaimer"
      defaults="<0>Click here</0> for integrated TRAIN+BUS travel passes"
      components={[
        <a
          href="https://www.autobus.it/arenaways-to/TPLWebPortal/it/showcase"
          target="_blank"
        />,
      ]}
    />
  ),
  'arenaways.ticketSent': (
    <Trans
      i18nKey="tenant.arenaways.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'twiliner.FIRST': (
    <Trans i18nKey="tenant.twiliner.FIRST" defaults="First class" />
  ),
  'twiliner.FOURTH': (
    <Trans i18nKey="tenant.twiliner.FOURTH" defaults="Fourth class" />
  ),
  'twiliner.SECOND': (
    <Trans i18nKey="tenant.twiliner.SECOND" defaults="Second class" />
  ),
  'twiliner.successMessage': (
    <Trans
      i18nKey="tenant.twiliner.successMessage"
      defaults="Your booking was successful! We look forward to welcoming you on board soon.<br /> <br />If you have any questions until then, please check our <faqLink>FAQ</faqLink> or contact our <mailToLink>customer support</mailToLink>."
      components={{
        faqLink: (
          <ExternalLink
            href="http://www.twiliner.com/faq"
            variant="body1-link"
          />
        ),
        mailToLink: (
          <ExternalLink
            href="mailto:service@twiliner.com"
            variant="body1-link"
          />
        ),
      }}
    />
  ),
  'twiliner.THIRD': (
    <Trans i18nKey="tenant.twiliner.THIRD" defaults="Third class" />
  ),
  'twiliner.travelPassDisclaimer': <></>,
  'twiliner.ticketSent': (
    <Trans
      i18nKey="tenant.twiliner.ticketSent"
      defaults="Your tickets have been sent to your email address. If you haven't received anything or can't find it in your spam folder, <mailToLink>please send us a message</mailToLink>."
      components={{
        mailToLink: (
          <ExternalLink
            href="mailto:service@twiliner.com"
            variant="body1-link"
          />
        ),
      }}
    />
  ),
  'sandbox-fs.FIRST': <Trans i18nKey="tenant.fs.FIRST" defaults="Executive" />,
  'sandbox-fs.FOURTH': (
    <Trans i18nKey="tenant.fs.FOURTH" defaults="Fourth class" />
  ),
  'sandbox-fs.SECOND': <Trans i18nKey="tenant.fs.SECOND" defaults="Business" />,
  'sandbox-fs.successMessage': (
    <Trans i18nKey="tenant.fs.successMessage" defaults="Success!" />
  ),
  'sandbox-fs.THIRD': <Trans i18nKey="tenant.fs.THIRD" defaults="Standard" />,
  'sandbox-fs.travelPassDisclaimer': <></>,
  'sandbox-fs.ticketSent': (
    <Trans
      i18nKey="tenant.fs.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'tifrance.FIRST': <Trans i18nKey="tenant.fs.FIRST" defaults="Executive" />,
  'tifrance.FOURTH': (
    <Trans i18nKey="tenant.fs.FOURTH" defaults="Fourth class" />
  ),
  'tifrance.SECOND': <Trans i18nKey="tenant.fs.SECOND" defaults="Business" />,
  'tifrance.successMessage': (
    <Trans i18nKey="tenant.fs.successMessage" defaults="Success!" />
  ),
  'tifrance.THIRD': <Trans i18nKey="tenant.fs.THIRD" defaults="Standard" />,
  'tifrance.travelPassDisclaimer': <></>,
  'tifrance.ticketSent': (
    <Trans
      i18nKey="tenant.fs.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'maas.FIRST': <Trans i18nKey="tenant.maas.FIRST" defaults="First class" />,
  'maas.FOURTH': <Trans i18nKey="tenant.maas.FOURTH" defaults="Fourth class" />,
  'maas.SECOND': <Trans i18nKey="tenant.maas.SECOND" defaults="Second class" />,
  'maas.successMessage': (
    <Trans i18nKey="tenant.maas.successMessage" defaults="Success!" />
  ),
  'maas.THIRD': <Trans i18nKey="tenant.maas.THIRD" defaults="Third class" />,
  'maas.travelPassDisclaimer': <></>,
  'maas.ticketSent': (
    <Trans
      i18nKey="tenant.maas.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'hellenictrain.FIRST': (
    <Trans i18nKey="tenant.hellenictrain.FIRST" defaults="First class" />
  ),
  'hellenictrain.FOURTH': (
    <Trans i18nKey="tenant.hellenictrain.FOURTH" defaults="Fourth class" />
  ),
  'hellenictrain.SECOND': (
    <Trans i18nKey="tenant.hellenictrain.SECOND" defaults="Second class" />
  ),
  'hellenictrain.successMessage': (
    <Trans i18nKey="tenant.hellenictrain.successMessage" defaults="Success!" />
  ),
  'hellenictrain.THIRD': (
    <Trans i18nKey="tenant.hellenictrain.THIRD" defaults="Third class" />
  ),
  'hellenictrain.travelPassDisclaimer': <></>,
  'hellenictrain.ticketSent': (
    <Trans
      i18nKey="tenant.hellenictrain.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'connectbus.FIRST': (
    <Trans i18nKey="tenant.connectbus.FIRST" defaults="First class" />
  ),
  'connectbus.FOURTH': (
    <Trans i18nKey="tenant.connectbus.FOURTH" defaults="Fourth class" />
  ),
  'connectbus.SECOND': (
    <Trans i18nKey="tenant.connectbus.SECOND" defaults="Second class" />
  ),
  'connectbus.successMessage': (
    <Trans i18nKey="tenant.connectbus.successMessage" defaults="Success!" />
  ),
  'connectbus.THIRD': (
    <Trans i18nKey="tenant.connectbus.THIRD" defaults="Third class" />
  ),
  'connectbus.travelPassDisclaimer': <></>,
  'connectbus.ticketSent': (
    <Trans
      i18nKey="tenant.connectbus.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'ltglink.FIRST': (
    <Trans i18nKey="tenant.ltglink.FIRST" defaults="First class" />
  ),
  'ltglink.FOURTH': (
    <Trans i18nKey="tenant.ltglink.FOURTH" defaults="Fourth class" />
  ),
  'ltglink.SECOND': (
    <Trans i18nKey="tenant.ltglink.SECOND" defaults="Second class" />
  ),
  'ltglink.successMessage': (
    <Trans i18nKey="tenant.ltglink.successMessage" defaults="Success!" />
  ),
  'ltglink.THIRD': (
    <Trans i18nKey="tenant.ltglink.THIRD" defaults="Third class" />
  ),
  'ltglink.travelPassDisclaimer': <></>,
  'ltglink.ticketSent': (
    <Trans
      i18nKey="tenant.ltglink.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
  'sandbox.FIRST': (
    <Trans i18nKey="tenant.sandbox.FIRST" defaults="First class" />
  ),
  'sandbox.FOURTH': (
    <Trans i18nKey="tenant.sandbox.FOURTH" defaults="Fourth class" />
  ),
  'sandbox.SECOND': (
    <Trans i18nKey="tenant.sandbox.SECOND" defaults="Second class" />
  ),
  'sandbox.successMessage': (
    <Trans i18nKey="tenant.sandbox.successMessage" defaults="Success!" />
  ),
  'sandbox.THIRD': (
    <Trans i18nKey="tenant.sandbox.THIRD" defaults="Third class" />
  ),
  'sandbox.travelPassDisclaimer': <></>,
  'sandbox.ticketSent': (
    <Trans
      i18nKey="tenant.sandbox.ticketSent"
      defaults="Your tickets have been sent to your email address."
    />
  ),
};

const TenantCopies = createTrans(tenantCopiesTranslations);

type TenantCopiesKeys = TransKeys<typeof TenantCopies>;

export const TransTenantSpecific: FC<{ i18nKey: TransTenantSpecificKeys }> = ({
  i18nKey,
}) => {
  const { value: tenant } = useSelector(tenantSelector);

  return <TenantCopies i18nKey={`${tenant}.${i18nKey}` as TenantCopiesKeys} />;
};
